<script setup lang="ts">
import { StackLayout } from '@/components/Layouts'
import AutocompleteField from '@/components/Selector/AutocompleteField.vue'
import { TypographyItem } from '@/components/Typography'
import organizationApi from '@/services/organization.service'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { PaginationDataType } from '@/types/PaginationType'
import { EDebounceTime } from '@/utils/debounceUtils'
import { useDebounceFn } from '@vueuse/core/index.cjs'
import { onMounted, ref, watch } from 'vue'

type Props = {
    associates: AssociatedDeviceModel[]
    isInEditMode: boolean
}
const props = defineProps<Props>()
defineEmits<{
    (e: 'refresh'): void
    (e: 'select', device: AssociatedDeviceModel): void
}>()

const device = ref()
watch(
    () => props.associates,
    () => (device.value = undefined)
)

const isLoadingDevices = ref(false)
const handleChange = (value: string) => {
    isLoadingDevices.value = true
    debouncedQuestion(value)
}
const debouncedQuestion = useDebounceFn((value: string) => getDevices(value), EDebounceTime.DEFAULT)
const getDevices = (name?: string) =>
    organizationApi
        .getMyDevices(name ? { filters: { name } } : {})
        .then(
            res =>
                (devicesOptions.value = (res as PaginationDataType<DeviceModel[]>).data.filter(
                    item =>
                        props.associates.find(associated => associated.id === item.id)
                            ? false
                            : item
                ))
        )
        .finally(() => (isLoadingDevices.value = false))

const devicesOptions = ref<DeviceModel[]>([])

onMounted(() => getDevices())
</script>

<template>
    <AutocompleteField
        v-if="isInEditMode"
        :label="$t('device.filters.name')"
        v-model="device"
        :loading="isLoadingDevices"
        @typing="handleChange"
        @select="$emit('select', $event as AssociatedDeviceModel)"
        :options="devicesOptions"
        class="width-fit">
        <template #default="props">
            <StackLayout direction="column">
                <TypographyItem :label="props.option.name" />
                <TypographyItem :label="`SN : ${props.option.serial_number}`" />
            </StackLayout>
        </template>
    </AutocompleteField>
</template>
